import {
  forwardRef,
  useImperativeHandle,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { AreaSelectBox } from "../FloorPlanPanelNew/AreaSelectBox";
import { Loader2 } from "lucide-react";
import { ZOOM_OPACITY_THRESHOLD } from "components/features/ProgressReportViewer/utils";
import { useObjectNavigation } from "../ObjectNavigationBar/useObjectNavigation";
import { ObjectNavigationBar } from "../ObjectNavigationBar";
import { ZoomControls } from "../ZoomControls";
import { PlanNameChip } from "../PlanNameChip";
import { SvgOverlay } from "../FloorPlanPanelNew/SvgOverlay";
import { useLatest } from "hooks/useLatest";
import {
  ZOOM_CONFIG,
  REFERENCE_IMAGE_SIZE,
  MIN_CIRCLE_SIZE_SCALE,
} from "../FloorPlanPanelNew/constants";
import { usePdfCanvasRenderer } from "./usePdfCanvasRenderer";
import { useMapInteraction } from "./useMapInteraction";
import { useZoomActions } from "./useZoomActions";
import { useFlashCanvas } from "./useFlashCanvas";
import { useFlashRipple } from "../hooks/useFlashRipple";
import { useKeyboardNavigation } from "./useKeyboardNavigation";
import { isDetectedObject } from "../FloorPlanPanelNew/types";
import type { FloorPlanPanelHandlers } from "../FloorPlanPanelNew/types";
import type { NavigableObject } from "../ObjectNavigationBar/types";
import type { FloorPlanPanelPdfProps } from "./types";

const RENDER_DEBOUNCE_MS = 350;

export const FloorPlanPanelPdf = forwardRef<
  FloorPlanPanelHandlers,
  FloorPlanPanelPdfProps
>(function FloorPlanPanelPdf(
  {
    plan,
    detectedObjects,
    selectedObject,
    setSelectedObject,
    isAutoZoomEnabled = true,
    showObjectNavigation = true,
    navigationObjects,
    onAfterNavigate,
    zoomToFitOnImageLoad = "always",
    renderToolbar,
    mode = "default",
    pickZoneCursor = "crosshair",
    exportActions,
    onMapClick,
    onMapMouseMove,
    onAreaSelect,
    onEmptyMapClick,
    backgroundObjects,
    floorPlanCablePaths,
    circuitPaths,
    routeCreationOverlay,
    polygonsOverlay,
    floorPlanLayoutOverlay,
    areNotesHidden,
    disableTransitions,
    rotation = 0,
    onRotate,
    onSelectedBboxChange,
  }: FloorPlanPanelPdfProps,
  ref,
) {
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const isFirstLoad = useRef(true);
  const isZoomedToObject = useRef(false);
  const minZoomLevel = useRef<number>(ZOOM_CONFIG.MIN);
  const renderDebounceRef = useRef<number | null>(null);
  const latestZoomToFitOnImageLoad = useLatest(zoomToFitOnImageLoad);
  const latestZoomLevel = useLatest(zoomLevel);
  const latestPanOffset = useLatest(panOffset);

  const svgOverlayRef = useRef<HTMLDivElement>(null);
  const planCanvasRef = useRef<HTMLCanvasElement>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const isPickZoneMode = mode === "pick_zone";

  const {
    isLoaded: pdfLoaded,
    pageWidth,
    pageHeight,
    tileRegion,
    render: renderPdf,
  } = usePdfCanvasRenderer({
    canvasRef: planCanvasRef,
    previewCanvasRef,
    containerRef,
    pdfUrl: plan.pdfUrl,
    pageNumber: plan.pageNumber,
    zoomLevel,
    panOffset,
    rotation,
  });

  const circleSizeScale = useMemo(() => {
    if (pageWidth <= 0 || pageHeight <= 0) return 1;
    return Math.max(
      Math.min(pageWidth, pageHeight) / REFERENCE_IMAGE_SIZE,
      MIN_CIRCLE_SIZE_SCALE,
    );
  }, [pageWidth, pageHeight]);

  const { zoomToFit, zoomToObject, zoomToBbox, zoomToElements } =
    useZoomActions({
      containerRef,
      pageWidth,
      pageHeight,
      setZoomLevel,
      setPanOffset,
      minZoomLevel,
    });

  const flashCanvas = useFlashCanvas({
    svgOverlayRef,
    canvasRef: planCanvasRef,
  });

  const flashRipple = useFlashRipple({
    svgOverlayRef,
  });

  const interaction = useMapInteraction({
    containerRef,
    zoomLevel,
    setZoomLevel,
    panOffset,
    setPanOffset,
    minZoomLevel,
    objects: detectedObjects,
    onObjectClick: setSelectedObject,
    isPickZoneMode,
    onMapClick,
    onMapMouseMove,
    onAreaSelect,
    onEmptyMapClick,
    isReady: pdfLoaded,
    circleSizeScale,
    circuitPaths,
  });

  const shouldAnimateTransition =
    !disableTransitions &&
    zoomLevel >= ZOOM_OPACITY_THRESHOLD &&
    !interaction.isDragging &&
    !interaction.isTouching &&
    !interaction.isWheelZooming;

  const handleAfterNavigate = useCallback(
    (object: NavigableObject) => {
      if (onAfterNavigate) {
        onAfterNavigate(object);
        return;
      }
      if (isDetectedObject(object)) {
        zoomToBbox(object.bbox);
      }
    },
    [onAfterNavigate, zoomToBbox],
  );

  const objectNavigation = useObjectNavigation<NavigableObject>({
    objects: navigationObjects ?? detectedObjects,
    selectedId: selectedObject?.id ?? null,
    onSelectId: setSelectedObject,
    onAfterSelect: handleAfterNavigate,
  });

  useKeyboardNavigation({
    goPrev: objectNavigation.goPrev,
    goNext: objectNavigation.goNext,
    isDisabled: !showObjectNavigation || isPickZoneMode,
  });

  const canvasWrapperTransformStyle = useMemo(
    () => ({
      transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${zoomLevel})`,
      transformOrigin: "top left" as const,
      transition: shouldAnimateTransition ? ZOOM_CONFIG.PAN_TRANSITION : "none",
      position: "relative" as const,
      width: `${pageWidth}px`,
      height: `${pageHeight}px`,
      opacity: pdfLoaded ? 1 : 0,
      pointerEvents: pdfLoaded ? ("auto" as const) : ("none" as const),
    }),
    [
      panOffset.x,
      panOffset.y,
      zoomLevel,
      shouldAnimateTransition,
      pageWidth,
      pageHeight,
      pdfLoaded,
    ],
  );

  const tilePositionStyle = useMemo(
    () =>
      tileRegion
        ? {
            left: `${tileRegion.pageLeft}px`,
            top: `${tileRegion.pageTop}px`,
          }
        : undefined,
    [tileRegion],
  );

  const scheduleRender = useCallback(() => {
    if (renderDebounceRef.current !== null) {
      clearTimeout(renderDebounceRef.current);
    }
    renderDebounceRef.current = window.setTimeout(() => {
      renderDebounceRef.current = null;
      renderPdf();
    }, RENDER_DEBOUNCE_MS);
  }, [renderPdf]);

  useImperativeHandle(
    ref,
    () => ({
      zoomToFit,
      zoomToObject,
      zoomToBbox,
      zoomToElements,
      flashCanvas,
      flashRipple,
      getZoomState: () => ({
        zoomLevel: latestZoomLevel.current,
        panOffset: latestPanOffset.current,
      }),
      setZoomState: (state) => {
        setZoomLevel(state.zoomLevel);
        setPanOffset(state.panOffset);
      },
    }),
    [
      zoomToFit,
      zoomToObject,
      zoomToBbox,
      zoomToElements,
      flashCanvas,
      flashRipple,
      latestZoomLevel,
      latestPanOffset,
    ],
  );

  useEffect(() => {
    isZoomedToObject.current = false;
  }, [selectedObject?.id]);

  // Auto-zoom to selected object
  useEffect(() => {
    if (
      isAutoZoomEnabled &&
      selectedObject &&
      pdfLoaded &&
      !isZoomedToObject.current
    ) {
      const timeoutId = setTimeout(() => {
        zoomToBbox(selectedObject.bbox);
        isZoomedToObject.current = true;
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [isAutoZoomEnabled, selectedObject, pdfLoaded, zoomToBbox]);

  // Initial zoom-to-fit when PDF loads
  useEffect(() => {
    if (!pdfLoaded || pageWidth <= 0 || pageHeight <= 0) return;

    const shouldZoom =
      latestZoomToFitOnImageLoad.current === "always" ||
      (latestZoomToFitOnImageLoad.current === "initial" && isFirstLoad.current);

    if (shouldZoom) zoomToFit();
    isFirstLoad.current = false;
  }, [pdfLoaded, pageWidth, pageHeight, latestZoomToFitOnImageLoad, zoomToFit]);

  // Re-render PDF on zoom/pan change
  useEffect(() => {
    if (!pdfLoaded) return;
    scheduleRender();
    return () => {
      if (renderDebounceRef.current !== null) {
        clearTimeout(renderDebounceRef.current);
      }
    };
  }, [pdfLoaded, zoomLevel, panOffset, scheduleRender]);

  useEffect(() => {
    return () => {
      if (renderDebounceRef.current !== null) {
        clearTimeout(renderDebounceRef.current);
      }
    };
  }, []);

  return (
    <div className="h-full bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
      <div className="relative h-full w-full flex">
        <div
          className="flex-1 relative bg-white rounded-lg border overflow-hidden w-full h-full"
          ref={containerRef}
          {...(pdfLoaded ? interaction.containerEventHandlers : {})}
          style={{
            cursor: !pdfLoaded
              ? "default"
              : interaction.isAreaSelecting
                ? "crosshair"
                : isPickZoneMode && pickZoneCursor === "crosshair"
                ? "crosshair"
                : interaction.isHoveringObject
                  ? "pointer"
                  : interaction.isDragging || interaction.isTouching
                    ? "grabbing"
                    : "grab",
            touchAction: "none",
            willChange: "contents",
          }}
        >
          <AreaSelectBox box={interaction.selectionBox} />
          <div style={canvasWrapperTransformStyle}>
            <canvas
              ref={previewCanvasRef}
              className="absolute top-0 left-0 select-none"
            />
            <canvas
              ref={planCanvasRef}
              className="absolute select-none"
              style={tilePositionStyle}
            />
          </div>
          {!pdfLoaded && (
            <div className="absolute inset-0 flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            </div>
          )}
          <div
            ref={svgOverlayRef}
            className="absolute inset-0 pointer-events-none"
          >
            {pdfLoaded && (
              <SvgOverlay
                objects={detectedObjects}
                backgroundObjects={backgroundObjects}
                selectedObject={selectedObject}
                onSelectedBboxChange={onSelectedBboxChange}
                onSelectNote={setSelectedObject}
                zoomLevel={zoomLevel}
                maxZoom={ZOOM_CONFIG.MAX}
                panOffset={panOffset}
                floorPlanCablePaths={floorPlanCablePaths}
                circuitPaths={circuitPaths}
                routeCreationOverlay={routeCreationOverlay}
                polygonsOverlay={polygonsOverlay}
                floorPlanLayoutOverlay={floorPlanLayoutOverlay}
                areNotesHidden={areNotesHidden}
                shouldAnimateTransition={shouldAnimateTransition}
                circleSizeScale={circleSizeScale}
                opacityThreshold={ZOOM_OPACITY_THRESHOLD}
              />
            )}
          </div>
        </div>
        {showObjectNavigation && (
          <ObjectNavigationBar
            canPrev={!isPickZoneMode && objectNavigation.canGoPrev}
            canNext={!isPickZoneMode && objectNavigation.canGoNext}
            onPrev={objectNavigation.goPrev}
            onNext={objectNavigation.goNext}
            onSelectIndex={objectNavigation.goToIndex}
            selectedIndex={objectNavigation.selectedIndex}
            total={objectNavigation.total}
          />
        )}
        <ZoomControls
          isDisabled={!pdfLoaded}
          onZoomIn={() => interaction.handleZoom("in")}
          onZoomOut={() => interaction.handleZoom("out")}
          onZoomToObject={
            selectedObject ? () => zoomToBbox(selectedObject.bbox) : undefined
          }
          onRotate={onRotate}
          onReset={zoomToFit}
        />
        <PlanNameChip
          name={plan.name}
          isArchived={plan.isArchived}
          exportActions={exportActions}
        />
        {renderToolbar?.({
          isDisabled: !pdfLoaded,
          isHoveringPlan: interaction.isHoveringPlan,
        })}
      </div>
    </div>
  );
});
